import {
  Dimension,
  getBoolean,
  getNumber,
  getTimestamp,
  getValue,
  putBoolean,
  putValue,
} from "./dimension";
import {
  BrachialPressure,
  Condition,
  DrinkingHabit,
  Length,
  Percent,
  Pressure,
  HabitualFrequency,
  SmokingHabit,
  Weight,
  SnusHabit,
  PhysicalActivityHabit,
  PolarResponse,
  HabitualStyle,
} from "./unit";

type Codec<T> = {
  toDimensions: (value: T) => Dimension[];
  toUnit: (dimensions: Dimension[]) => T;
};

export function plain<K extends string, T extends Record<K, number>>(
  unit: K
): Codec<T> {
  return {
    toDimensions(value) {
      return [putValue("value", value[unit], unit)];
    },
    toUnit(dimensions) {
      return {
        [unit]: getValue(dimensions, "value"),
      } as T;
    },
  };
}

export const cm: Codec<Length> = {
  toDimensions(length) {
    return [putValue("value", length.meters * 100, "cm")];
  },
  toUnit(dimensions) {
    return {
      meters: getNumber(dimensions, "value") / 100,
    };
  },
};

export const kg: Codec<Weight> = {
  toDimensions(weight) {
    return [putValue("value", weight.kilograms, "kg")];
  },
  toUnit(dimensions) {
    return {
      kilograms: getNumber(dimensions, "value"),
    };
  },
};

export const percent: Codec<Percent> = {
  toDimensions(unit) {
    return [putValue("value", unit.percent, "%")];
  },
  toUnit(dimensions) {
    return {
      percent: getNumber(dimensions, "value"),
    };
  },
};

export const pressure: Codec<Pressure> = {
  toDimensions(pressure) {
    return [putValue("value", pressure.mmHg, "mm Hg")];
  },
  toUnit(dimensions) {
    return {
      mmHg: getNumber(dimensions, "value"),
    };
  },
};

export const brachialPressure: Codec<BrachialPressure> = {
  toDimensions(brachial) {
    return [
      {
        name: "systolic",
        unit: "mm Hg",
        value: brachial.systolic.mmHg,
      },
      {
        name: "diastolic",
        unit: "mm Hg",
        value: brachial.diastolic.mmHg,
      },
    ];
  },
  toUnit(dimensions) {
    return {
      diastolic: {
        mmHg: getNumber(dimensions, "diastolic"),
      },
      systolic: {
        mmHg: getNumber(dimensions, "systolic"),
      },
    };
  },
};

export const boolean: Codec<boolean | undefined> = {
  toDimensions(value) {
    return [putBoolean("value", value)];
  },
  toUnit(dimensions) {
    return getBoolean(dimensions, "value");
  },
};

export const smoking: Codec<SmokingHabit> = {
  toDimensions(habit) {
    const dimensions: Dimension[] = [];

    if (habit.style != null) {
      dimensions.push(putValue("habitual-style", habit.style));
    }

    if (habit.frequency != null) {
      dimensions.push(putValue("frequency", habit.frequency));
    }

    if (habit.dailyCigarettes != null) {
      dimensions.push(putValue("daily-cigarette-count", habit.dailyCigarettes));
    }

    if (habit.quitSmokingAt != null) {
      dimensions.push(
        putValue("quit-smoking-recency", habit.quitSmokingAt.getTime())
      );
    }

    return dimensions;
  },

  toUnit(dimensions) {
    return {
      style: getValue(dimensions, "habitual-style"),
      dailyCigarettes: getValue(dimensions, "daily-cigarette-count"),
      quitSmokingAt: getTimestamp(dimensions, "quit-smoking-recency"),
      frequency: getValue(dimensions, "frequency"),
    };
  },
};

export const snus: Codec<SnusHabit> = {
  toDimensions(habit) {
    const dimensions: Dimension[] = [];

    if (habit.style != null) {
      dimensions.push(putValue("habitual-style", habit.style));
    }

    if (habit.frequency != null) {
      dimensions.push(putValue("frequency", habit.frequency));
    }

    if (habit.dailyCans != null) {
      dimensions.push(putValue("daily-can-count", habit.dailyCans));
    }

    if (habit.quitSnusingAt != null) {
      dimensions.push(
        putValue("quit-snusing-recency", habit.quitSnusingAt.getTime())
      );
    }

    return dimensions;
  },

  toUnit(dimensions) {
    return {
      style: getValue(dimensions, "habitual-style"),
      dailyCans: getValue(dimensions, "daily-can-count"),
      quitSnusingAt: getTimestamp(dimensions, "quit-snusing-recency"),
      frequency: getValue(dimensions, "frequency"),
    };
  },
};

export const drinking: Codec<DrinkingHabit> = {
  toDimensions(habit) {
    const dim: Dimension[] = [
      putBoolean("drinks-beer", habit.beer),
      putBoolean("drinks-wine", habit.wine),
      putBoolean("drinks-spirits", habit.spirits),
      putBoolean("drinks-cocktails", habit.cocktails),
      putBoolean("drinks-liqour", habit.liquor),
    ];
    if (habit.weeklyDrinks != null && isFinite(habit.weeklyDrinks)) {
      dim.push(putValue("weekly-drink-count", habit.weeklyDrinks, null));
    }
    return dim;
  },

  toUnit(dimensions) {
    return {
      liquor: getBoolean(dimensions, "drinks-liqour"),
      beer: getBoolean(dimensions, "drinks-beer"),
      cocktails: getBoolean(dimensions, "drinks-cocktails"),
      spirits: getBoolean(dimensions, "drinks-spirits"),
      wine: getBoolean(dimensions, "drinks-wine"),
      weeklyDrinks: getValue(dimensions, "weekly-drink-count"),
    };
  },
};

export const condition: Codec<Set<Condition>> = {
  toDimensions(conditions) {
    return Array.from(conditions).map((condition) => {
      return putBoolean(condition, true);
    });
  },

  toUnit(dimensions) {
    return new Set(
      dimensions.map((dimension) => {
        return dimension.name as Condition;
      })
    );
  },
};

export const physicalActivity: Codec<PhysicalActivityHabit> = {
  toDimensions(habit) {
    const dim: Dimension[] = [];
    if (
      habit.weeklyExerciseHours != null &&
      isFinite(habit.weeklyExerciseHours)
    ) {
      dim.push(
        putValue("frequency", habit.weeklyExerciseHours, "hours weekly")
      );
    }
    if (habit.wearsHealthTracker != null) {
      dim.push(putBoolean("wears-health-tracker", habit.wearsHealthTracker));
    }
    return dim;
  },

  toUnit(dimensions) {
    return {
      wearsHealthTracker: getBoolean(dimensions, "wears-health-tracker"),
      weeklyExerciseHours: getValue(dimensions, "frequency"),
    };
  },
};

export const highBloodPressure: Codec<{ diagnosed?: PolarResponse }> = {
  toDimensions(bloodpressure) {
    if (bloodpressure.diagnosed != null) {
      return [putValue("diagnosed", bloodpressure.diagnosed, "polar")];
    }
    return [];
  },

  toUnit(dimensions) {
    return {
      diagnosed: getValue(dimensions, "diagnosed") as PolarResponse | undefined,
    };
  },
};
